import { useEffect, useState, Fragment } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fmtCur, fmtNum, fmtDate } from "../lib/format";
import { WmPart } from "../lib/wmParts";
import api from "../lib/api";
import { toast } from "sonner";
import { PencilSimple, Check, X, ArrowsClockwise, FileText, Prohibit, ArrowCounterClockwise, Plus, WarningCircle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { LineEditChip } from "./EditedPill";

const emptyNewLine = { icode: "", name: "", qty_ordered: "", price: "", deliv_date: "" };

// Popup showing a PO's full line detail — opens in place (no navigation). Lines are inline-editable
// (due date, ordered qty, unit price) for real SAP/Excel/ERP POs exactly like the Open Orders grid:
// writes Mongo (authoritative) then best-effort to the live ERP, with a per-line ↻ Sync retry.
// Admins can also cancel (partial/full) the open qty of a line — non-destructive (writes
// dbo.poi.CancelQty + dbo.po.POStatus) with a reason, and un-cancel to restore.
export default function PoDetailModal({ po, onClose, loading, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user && ["admin", "super_admin", "manager", "po_dispatch"].includes(user.role);
  const th = { textAlign: "left", padding: "11px 14px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#475569", borderBottom: "2px solid var(--jde-border)", whiteSpace: "nowrap", background: "#F1F5F9" };
  const td = { padding: "11px 14px", fontSize: 13.5, borderBottom: "1px solid #EEF1F4", verticalAlign: "middle" };
  // Crisp, generously-sized inline-edit controls (replaces the old cramped 11px cells)
  const editInput = { fontSize: 13.5, padding: "7px 10px", borderRadius: 7, border: "1.5px solid #93C5FD", outline: "none", background: "#fff", boxShadow: "0 0 0 3px rgba(59,130,246,0.12)" };
  const iconBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 30, height: 30, borderRadius: 7, border: "1px solid var(--jde-border)", background: "#fff", cursor: "pointer", padding: 0, transition: "background 0.12s, border-color 0.12s, color 0.12s" };
  const cur = (po && (po.currency || po.currency_code)) || "USD";

  const [rows, setRows] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [custpoVal, setCustpoVal] = useState("");
  const [custpoBusy, setCustpoBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resyncIdx, setResyncIdx] = useState(null);
  const [srcDoc, setSrcDoc] = useState(null);   // { has_doc, url, name }
  const [hdr, setHdr] = useState({});           // { cancelled, cancel_reason, cancelled_by, status }
  const [cancelIdx, setCancelIdx] = useState(null);   // line index currently being cancelled
  const [cancelQty, setCancelQty] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newLine, setNewLine] = useState(emptyNewLine);
  const [addBusy, setAddBusy] = useState(false);

  useEffect(() => {
    setRows((po && po.lines) || []);
    setEditMode(false);
    setCustpoVal((po && po.ms_custpo) || "");
    setCancelIdx(null);
    setAdding(false);
    setNewLine(emptyNewLine);
    setHdr(po ? { cancelled: po.cancelled, cancel_reason: po.cancel_reason, cancelled_by: po.cancelled_by, status: po.status } : {});
  }, [po]);

  // Look up the permanent public link to this PO's source document (PDF/image), if one exists.
  useEffect(() => {
    setSrcDoc(null);
    const id = po && po.id;
    if (!id) return;
    let alive = true;
    api.get(`/po/${id}/source-doc`).then((r) => { if (alive) setSrcDoc(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [po]);

  const pono = po && po.ms_pono;
  const editable = !!pono;   // real ERP/Excel POs have an ms_pono (intake POs are edited via /po/{id})

  const erpToast = (label, erp) => {
    if (erp?.ok && erp?.matched) toast.success(`${label} — live ERP synced \u2713`);
    else toast.error(`${label} saved, but ERP NOT updated: ${erp?.reason || "unmatched"}. Use the \u21bb Sync button to retry.`);
  };
  const save = async (idx, field, value) => {
    setBusy(true);
    try {
      let r;
      if (field === "deliv") r = await api.patch(`/ms/pos/${pono}/line-due-date`, { line_index: idx, deliv_date: value || null });
      else if (field === "qty") {
        const n = Number(value); if (!Number.isFinite(n) || n < 0) { toast.error("Enter a valid quantity"); return; }
        r = await api.patch(`/ms/pos/${pono}/line-qty`, { line_index: idx, qty_ordered: n });
      } else {
        const n = Number(value); if (!Number.isFinite(n) || n < 0) { toast.error("Enter a valid price"); return; }
        r = await api.patch(`/ms/pos/${pono}/line-price`, { line_index: idx, price: n });
      }
      erpToast(field === "deliv" ? "Due date updated" : field === "qty" ? "Quantity updated" : "Unit price updated", r.data?.erp_writeback);
      setRows(r.data?.lines || rows);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save (admin only)");
    } finally { setBusy(false); }
  };
  const saveCustpo = async () => {
    const v = custpoVal.trim();
    if (!v || v === ((po && po.ms_custpo) || "")) return;
    setCustpoBusy(true);
    try {
      const r = await api.patch(`/ms/pos/${pono}/custpo`, { custpo: v });
      erpToast("Customer PO updated", r.data?.erp_writeback);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update customer PO");
      setCustpoVal((po && po.ms_custpo) || "");
    } finally { setCustpoBusy(false); }
  };
  const resync = async (idx) => {
    setResyncIdx(idx);
    try {
      const r = await api.post(`/ms/pos/${pono}/line-erp-sync`, { line_index: idx });
      if (r.data?.ok) toast.success("Line pushed to live ERP \u2713 (due date, qty, price)");
      else {
        const bad = Object.entries(r.data?.results || {}).filter(([, v]) => !v.matched).map(([k, v]) => `${k}: ${v.reason || "unmatched"}`).join("; ");
        toast.error(`ERP sync incomplete \u2014 ${bad}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "ERP sync failed (admin only)");
    } finally { setResyncIdx(null); }
  };

  const openCancel = (idx) => {
    const l = rows[idx] || {};
    const maxCancel = Math.max(0, (Number(l.qty_ordered) || 0) - (Number(l.qty_shipped) || 0));
    setCancelIdx(idx);
    setCancelQty(String(l.cancel_qty || maxCancel));   // default to full open cancel
    setCancelReason("");
  };
  const closeCancel = () => { setCancelIdx(null); setCancelQty(""); setCancelReason(""); };

  const applyResult = (data) => {
    setRows(data?.lines || rows);
    setHdr({ cancelled: data?.cancelled, cancel_reason: data?.cancel_reason, cancelled_by: data?.cancelled_by, status: data?.status });
    const erp = data?.erp_writeback || {};
    const lineErp = (erp.lines || [])[0];
    const okAll = (erp.lines || []).every((x) => x.matched) && (erp.po_status ? erp.po_status.ok : true);
    if (okAll) toast.success(`Cancellation applied — live ERP synced \u2713${erp.full_cancel ? " (PO cancelled)" : ""}`);
    else toast.error(`Cancellation saved, but ERP write incomplete: ${lineErp?.reason || erp.po_status?.reason || "unmatched"}`);
    onChanged && onChanged();
  };

  const submitCancel = async (idx) => {
    const n = Number(cancelQty);
    if (!Number.isFinite(n) || n < 0) { toast.error("Enter a valid cancel quantity"); return; }
    if (!cancelReason.trim()) { toast.error("A cancellation reason is required"); return; }
    setCancelBusy(true);
    try {
      const r = await api.post(`/ms/pos/${pono}/cancel`, { reason: cancelReason.trim(), lines: [{ line_index: idx, cancel_qty: n }] });
      applyResult(r.data);
      closeCancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel (admin only)");
    } finally { setCancelBusy(false); }
  };

  const uncancel = async () => {
    if (!window.confirm("Restore this PO? This zeroes all cancelled quantities and reopens the order (also clears dbo.poi.CancelQty in the ERP).")) return;
    setCancelBusy(true);
    try {
      const r = await api.post(`/ms/pos/${pono}/uncancel`, { reason: "restored via PO detail" });
      applyResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to un-cancel (admin only)");
    } finally { setCancelBusy(false); }
  };

  const anyCancelled = rows.some((l) => (Number(l.cancel_qty) || 0) > 0 || l.cancelled);

  const [resyncPoBusy, setResyncPoBusy] = useState(false);
  const doResync = async (force) => {
    setResyncPoBusy(true);
    try {
      const r = await api.post(`/ms/pos/${pono}/resync-from-erp${force ? "?force=true" : ""}`);
      setRows(r.data?.lines || rows);
      setHdr({ cancelled: r.data?.cancelled, cancel_reason: r.data?.cancel_reason, cancelled_by: r.data?.cancelled_by, status: r.data?.status });
      toast.success(`Resynced from ERP \u2713 (${r.data?.resync?.lines_from_erp ?? "?"} lines)`);
      onChanged && onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && detail.code === "erp_sync_pending") {
        const items = (detail.pending || []).map((p) => `\u2022 ${p.key}${p.reason ? ` \u2014 ${p.reason}` : ""}`).join("\n");
        const proceed = window.confirm(`\u26A0\uFE0F ${detail.message}\n\nUnsynced edits:\n${items}\n\nResync anyway and DISCARD these edits?`);
        if (proceed) { await doResync(true); return; }
        toast.message("Resync cancelled \u2014 your unsynced edits are kept");
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Resync failed (admin only)");
      }
    } finally { setResyncPoBusy(false); }
  };
  const resyncFromErp = async () => {
    if (!window.confirm("Re-pull this PO's lines from the live ERP and reaffirm them here? The ERP is the source of truth for lines/quantities/cancellations; app-only notes on matching lines are kept.")) return;
    await doResync(false);
  };

  const openAdd = () => { setNewLine(emptyNewLine); setAdding(true); };
  const closeAdd = () => { setAdding(false); setNewLine(emptyNewLine); };
  const submitAdd = async () => {
    const icode = (newLine.icode || "").trim();
    const qty = Number(newLine.qty_ordered);
    const price = Number(newLine.price || 0);
    if (!icode) { toast.error("Item code is required"); return; }
    if (!Number.isFinite(qty) || qty <= 0) { toast.error("Enter a valid ordered quantity"); return; }
    if (!Number.isFinite(price) || price < 0) { toast.error("Enter a valid unit price"); return; }
    setAddBusy(true);
    try {
      const r = await api.post(`/ms/pos/${pono}/add-line`, {
        icode, name: (newLine.name || "").trim() || null,
        qty_ordered: qty, price, deliv_date: newLine.deliv_date || null,
      });
      erpToast("Line added", r.data?.erp_writeback);
      setRows(r.data?.lines || rows);
      closeAdd();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add line (admin only)");
    } finally { setAddBusy(false); }
  };

  // View mode -> plain text. Edit mode -> an uncontrolled input that autosaves on blur / Enter
  // (no per-field pencil; the single Edit toggle in the header turns all fields editable at once).
  const CellEdit = ({ idx, field, display, type = "text", step, initial }) => {
    if (!editMode || !editable) return <>{display}</>;
    return (
      <input defaultValue={initial == null ? "" : String(initial)} type={type} step={step} min={type === "number" ? "0" : undefined}
        data-testid={`pomodal-${field}-input-${idx}`} disabled={busy}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        onBlur={(e) => { const v = e.target.value; if (String(v) !== (initial == null ? "" : String(initial))) save(idx, field, v); }}
        style={{ ...editInput, width: type === "number" ? 82 : type === "date" ? 130 : 130, textAlign: type === "number" ? "right" : "left" }} />
    );
  };

  return (
    <Dialog open={!!(po || loading)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl" data-testid="po-detail-modal">
        <style>{`
          .pomodal-edit-btn:hover { background: #2563EB !important; border-color: #2563EB !important; color: #fff !important; }
          [data-testid="po-detail-modal"] .jde-badge { line-height: 1.4; }
        `}</style>
        {loading && !po && <div className="muted" style={{ padding: 24, textAlign: "center" }}>Loading PO…</div>}
        {po && (
          <>
            <DialogHeader>
              <DialogTitle style={{ fontSize: 16, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, paddingRight: 44 }}>
                <span>PO {po.ms_pono}</span>
                {editMode ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span className="muted" style={{ fontWeight: 500, fontSize: 12 }}>Customer PO</span>
                    <input defaultValue={po.ms_custpo || ""} onChange={(e) => setCustpoVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} onBlur={saveCustpo} disabled={custpoBusy}
                      data-testid="pomodal-custpo-input" placeholder="Customer PO #"
                      style={{ ...editInput, width: 190, fontSize: 13.5, fontWeight: 600 }} />
                  </span>
                ) : (
                  po.ms_custpo && <span className="muted" style={{ fontWeight: 500, fontSize: 13 }}>Customer PO {po.ms_custpo}</span>
                )}
                {po.erp_sync_pending && (
                  <span data-testid="pomodal-erp-pending-badge" title={(po.erp_sync_pending_items || []).map((p) => `${p.key}${p.reason ? " \u2014 " + p.reason : ""}`).join("\n") || "Some edits were not written to the ERP"}
                    style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 999, fontWeight: 700, fontSize: 11.5, color: "#9A3412", background: "#FFEDD5", border: "1px solid #FDBA74" }}>
                    <WarningCircle size={13} weight="bold" /> {(po.erp_sync_pending_items || []).length || ""} unsynced edit{((po.erp_sync_pending_items || []).length === 1) ? "" : "s"}
                  </span>
                )}
                {isAdmin && editable && (
                  <button data-testid="pomodal-resync-erp-btn" onClick={resyncFromErp} disabled={resyncPoBusy}
                    title="Re-pull this PO's lines from the live ERP (source of truth)"
                    style={{ marginLeft: po.erp_sync_pending ? 8 : "auto", display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 8, border: "1.5px solid #C7D2FE", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#4338CA", background: "#EEF2FF" }}>
                    <ArrowsClockwise size={16} weight="bold" /> {resyncPoBusy ? "Resyncing\u2026" : "Resync from ERP"}
                  </button>
                )}
                {isAdmin && editable && (
                  <button data-testid="pomodal-edit-toggle" onClick={() => setEditMode((m) => !m)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: editMode ? "#059669" : "#2563EB" }}>
                    {editMode ? <><Check size={16} weight="bold" /> Done editing</> : <><PencilSimple size={16} weight="bold" /> Edit</>}
                  </button>
                )}
              </DialogTitle>
              <DialogDescription className="sr-only">Purchase order line-item detail with inline editing of quantity, price, due date and customer PO.</DialogDescription>
            </DialogHeader>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 12, color: "#334155", marginBottom: 8 }}>
              <span><span className="muted">Customer:</span> <strong>{po.customer_name}</strong></span>
              <span><span className="muted">PO Date:</span> <strong>{fmtDate(po.po_date)}</strong></span>
              {po.amount != null && <span><span className="muted">Amount:</span> <strong>{fmtCur(po.amount, cur)}</strong></span>}
              {po.rev_no > 0 && <span><span className="muted">Rev:</span> <strong>R{po.rev_no}</strong></span>}
              {srcDoc?.has_doc && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <a href={srcDoc.url} target="_blank" rel="noopener noreferrer" data-testid="pomodal-source-doc-link"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#2563EB", fontWeight: 700, textDecoration: "none" }}>
                    <FileText size={14} weight="bold" /> View source document
                  </a>
                  <button className="jde-btn jde-btn-sm" title="Copy public link" data-testid="pomodal-source-doc-copy"
                    onClick={() => { navigator.clipboard?.writeText(srcDoc.url); toast.success("Source document link copied"); }}>Copy link</button>
                </span>
              )}
              {editable && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 999, background: editMode ? "#ECFDF5" : "#EFF6FF", border: `1px solid ${editMode ? "#A7F3D0" : "#BFDBFE"}`, color: editMode ? "#047857" : "#1D4ED8", fontSize: 11.5, fontWeight: 600 }}>
                  <PencilSimple size={13} weight="bold" />
                  {editMode ? "Editing — click a field, then Tab/Enter to save. Changes sync to the live ERP." : "Click Edit to change quantities, prices, due dates & the customer PO."}
                </span>
              )}
            </div>
            {(hdr.cancelled || anyCancelled) && (
              <div data-testid="pomodal-cancel-banner" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "8px 12px", marginBottom: 8, borderRadius: 6, background: "#FEF2F2", border: "1px solid #FECACA" }}>
                <div style={{ fontSize: 12, color: "#991B1B" }}>
                  <strong style={{ textTransform: "uppercase", letterSpacing: 0.3 }}>
                    {hdr.cancelled ? "PO Cancelled" : "Partially cancelled"}
                  </strong>
                  {hdr.cancel_reason && <span> — {hdr.cancel_reason}</span>}
                  {hdr.cancelled_by && <span className="muted" style={{ marginLeft: 6 }}>(by {hdr.cancelled_by})</span>}
                </div>
                {isAdmin && editable && (
                  <button className="jde-btn jde-btn-sm" disabled={cancelBusy} onClick={uncancel} data-testid="pomodal-uncancel-btn"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#166534", borderColor: "#BBF7D0" }}>
                    <ArrowCounterClockwise size={12} weight="bold" /> Un-cancel / restore
                  </button>
                )}
              </div>
            )}
            <div style={{ maxHeight: "55vh", overflow: "auto", border: "1px solid var(--jde-border)", borderRadius: 6 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", background: "white" }}>
                <thead style={{ position: "sticky", top: 0, background: "#F8FAFC", zIndex: 1 }}><tr>
                  {["Item", "Description", "Ordered", "Shipped", "Open", "Unit Price", "Due", "Ship Date", "Amount", "Cancel", ""].map((h, i) => (
                    <th key={h + i} style={{ ...th, textAlign: (i >= 2 && i <= 5) || i === 8 ? "right" : "left" }}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {rows.map((l, idx) => {
                    const cq = Number(l.cancel_qty) || 0;
                    const canCancel = isAdmin && editable && ((Number(l.qty_open) || 0) > 0 || cq > 0);
                    return (
                    <Fragment key={idx}>
                    <tr style={l.cancelled ? { background: "#FEF2F2" } : undefined}>
                      <td style={{ ...td, fontWeight: 700 }} className="text-mono">{l.icode || "—"}<WmPart icode={l.icode} /><LineEditChip edit={l.last_edit} idx={idx} /></td>
                      <td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={l.name || ""}>{l.name || "—"}</td>
                      <td style={{ ...td, textAlign: "right" }} className="text-mono"><CellEdit idx={idx} field="qty" type="number" step="1" display={fmtNum(l.qty_ordered)} initial={l.qty_ordered} /></td>
                      <td style={{ ...td, textAlign: "right", color: "#0F766E" }} className="text-mono">{fmtNum(l.qty_shipped)}</td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 700, color: (Number(l.qty_open) || 0) > 0 ? "#B91C1C" : "#166534" }} className="text-mono">{fmtNum(l.qty_open)}</td>
                      <td style={{ ...td, textAlign: "right" }} className="text-mono"><CellEdit idx={idx} field="price" type="number" step="0.00001" display={l.price ? fmtCur(l.price, cur) : "—"} initial={l.price} /></td>
                      <td style={td} className="text-mono"><CellEdit idx={idx} field="deliv" type="date" display={l.deliv_date ? fmtDate(l.deliv_date) : "—"} initial={(l.deliv_date || "").slice(0, 10)} /></td>
                      <td style={{ ...td, color: "#0F766E" }} className="text-mono" data-testid={`po-modal-shipdate-${idx}`}>
                        {(Number(l.qty_shipped) || 0) > 0 && l.dis_date ? fmtDate(l.dis_date) : "—"}
                        {Array.isArray(l.inv_nos) && l.inv_nos.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            <span className="jde-badge" title={`Shipped on invoice ${l.inv_nos.join(", ")}`}
                              style={{ background: "#EEF2FF", color: "#3730A3", fontWeight: 700, fontSize: 10.5, padding: "2px 7px", borderRadius: 8 }}
                              data-testid={`po-modal-invno-${idx}`}>
                              Inv {l.inv_nos.length === 1 ? l.inv_nos[0] : `${l.inv_nos[0]} +${l.inv_nos.length - 1}`}
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: "right" }} className="text-mono">{fmtCur((l.price != null && l.price !== "" && l.qty_ordered != null && l.qty_ordered !== "") ? (Number(l.price) || 0) * (Number(l.qty_ordered) || 0) : (l.amt || 0), cur)}</td>
                      <td style={{ ...td, textAlign: "center", whiteSpace: "nowrap" }} className="text-mono">
                        {cq > 0 && (
                          <span data-testid={`pomodal-cancelqty-${idx}`} style={{ color: "#B91C1C", fontWeight: 700 }} title="Cancelled quantity">
                            {fmtNum(cq)}{l.cancelled && <span style={{ fontSize: 9, marginLeft: 4, padding: "0 4px", borderRadius: 4, background: "#FEE2E2", color: "#991B1B" }}>FULL</span>}
                          </span>
                        )}
                        {canCancel && cancelIdx !== idx && (
                          <button style={{ ...iconBtn, width: 26, height: 26, marginLeft: cq > 0 ? 8 : 0, color: "#B91C1C", borderColor: "#FECACA", background: "#FEF2F2" }}
                            title="Cancel open qty on this line" onClick={() => openCancel(idx)} data-testid={`pomodal-cancel-btn-${idx}`}><Prohibit size={14} weight="bold" /></button>
                        )}
                        {!canCancel && cq === 0 && <span className="muted">—</span>}
                      </td>
                      <td style={{ ...td, textAlign: "center" }}>
                        {editable && (
                          <button style={{ ...iconBtn, width: 26, height: 26, color: "#475569", opacity: resyncIdx === idx ? 0.5 : 1 }} disabled={resyncIdx === idx}
                            title="Push this line's due date, qty & price to the live ERP (retry if out of sync)"
                            onClick={() => resync(idx)} data-testid={`pomodal-erp-sync-${idx}`}><ArrowsClockwise size={14} weight="bold" /></button>
                        )}
                      </td>
                    </tr>
                    {cancelIdx === idx && (
                      <tr key={`${idx}-cancel`}>
                        <td colSpan={11} style={{ ...td, background: "#FFF7F7", borderBottom: "2px solid #FECACA", padding: "14px 16px" }}>
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
                            <strong style={{ fontSize: 13.5, color: "#991B1B" }}>Cancel {l.icode}</strong>
                            <span className="muted" style={{ fontSize: 12 }}>Open now: {fmtNum(l.qty_open)} · max cancellable: {fmtNum(Math.max(0, (Number(l.qty_ordered) || 0) - (Number(l.qty_shipped) || 0)))}</span>
                            <label style={{ fontSize: 12.5, fontWeight: 600, color: "#7F1D1D", display: "inline-flex", alignItems: "center", gap: 7 }}>Cancel qty
                              <input type="number" min="0" value={cancelQty} onChange={(e) => setCancelQty(e.target.value)} data-testid={`pomodal-cancel-qty-input-${idx}`}
                                style={{ ...editInput, borderColor: "#FCA5A5", boxShadow: "0 0 0 3px rgba(239,68,68,0.10)", width: 100, textAlign: "right" }} />
                            </label>
                            <input type="text" placeholder="Reason (required)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid={`pomodal-cancel-reason-input-${idx}`}
                              style={{ ...editInput, borderColor: "#FCA5A5", boxShadow: "0 0 0 3px rgba(239,68,68,0.10)", flex: "1 1 240px", minWidth: 200 }} />
                            <button disabled={cancelBusy} onClick={() => submitCancel(idx)} data-testid={`pomodal-cancel-confirm-${idx}`}
                              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 8, border: "none", background: "#DC2626", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}><Prohibit size={15} weight="bold" /> Confirm cancel</button>
                            <button disabled={cancelBusy} onClick={closeCancel} data-testid={`pomodal-cancel-abort-${idx}`}
                              style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid var(--jde-border)", background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Dismiss</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );})}
                  {rows.length === 0 && <tr><td colSpan={11} className="muted" style={{ padding: 16, textAlign: "center" }}>No line items.</td></tr>}
                </tbody>
              </table>
            </div>
            {isAdmin && editable && (
              <div style={{ marginTop: 12 }}>
                {!adding ? (
                  <button onClick={openAdd} data-testid="pomodal-add-line-btn"
                    style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 8, border: "1.5px dashed #93C5FD", background: "#EFF6FF", color: "#1D4ED8", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                    <Plus size={15} weight="bold" /> Add line
                  </button>
                ) : (
                  <div data-testid="pomodal-add-line-form" style={{ padding: "14px 16px", borderRadius: 10, background: "#F0F9FF", border: "1.5px solid #BAE6FD" }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: "#075985", marginBottom: 10 }}>Add a new line to this PO</div>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12 }}>
                      <label style={{ fontSize: 11.5, fontWeight: 600, color: "#334155" }}>Item code
                        <input value={newLine.icode} onChange={(e) => setNewLine({ ...newLine, icode: e.target.value })} data-testid="pomodal-add-icode"
                          placeholder="e.g. MAZ42117243" style={{ ...editInput, display: "block", marginTop: 4, width: 190 }} />
                      </label>
                      <label style={{ fontSize: 11.5, fontWeight: 600, color: "#334155", flex: "1 1 220px" }}>Description
                        <input value={newLine.name} onChange={(e) => setNewLine({ ...newLine, name: e.target.value })} data-testid="pomodal-add-name"
                          placeholder="Description" style={{ ...editInput, display: "block", marginTop: 4, width: "100%" }} />
                      </label>
                      <label style={{ fontSize: 11.5, fontWeight: 600, color: "#334155" }}>Ordered qty
                        <input type="number" min="0" value={newLine.qty_ordered} onChange={(e) => setNewLine({ ...newLine, qty_ordered: e.target.value })} data-testid="pomodal-add-qty"
                          style={{ ...editInput, display: "block", marginTop: 4, width: 110, textAlign: "right" }} />
                      </label>
                      <label style={{ fontSize: 11.5, fontWeight: 600, color: "#334155" }}>Unit price
                        <input type="number" min="0" step="0.00001" value={newLine.price} onChange={(e) => setNewLine({ ...newLine, price: e.target.value })} data-testid="pomodal-add-price"
                          style={{ ...editInput, display: "block", marginTop: 4, width: 110, textAlign: "right" }} />
                      </label>
                      <label style={{ fontSize: 11.5, fontWeight: 600, color: "#334155" }}>Due date
                        <input type="date" value={newLine.deliv_date} onChange={(e) => setNewLine({ ...newLine, deliv_date: e.target.value })} data-testid="pomodal-add-deliv"
                          style={{ ...editInput, display: "block", marginTop: 4, width: 150 }} />
                      </label>
                      <button disabled={addBusy} onClick={submitAdd} data-testid="pomodal-add-confirm"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 8, border: "none", background: "#0284C7", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
                        <Check size={15} weight="bold" /> {addBusy ? "Adding…" : "Add line"}
                      </button>
                      <button disabled={addBusy} onClick={closeAdd} data-testid="pomodal-add-abort"
                        style={{ padding: "9px 16px", borderRadius: 8, border: "1px solid var(--jde-border)", background: "#fff", color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
                    </div>
                    <div className="muted" style={{ fontSize: 11.5, marginTop: 8 }}>The line is saved in the app and inserted into the live ERP (dbo.poi).</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
